'use client'

import { useTranslations } from 'next-intl'
import { CSSProperties } from 'react'

import { ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Checkbox, Table } from '@radix-ui/themes'

import { useTableContext, useTableStore } from '../context/table-context'
import { generateAccessorColumn } from './table-filters-popover/filter'
import { TLeadingCell, useTableLayout } from './table-utils'
import useUpdateEffect from '../../hooks/update-effect'

const SelectAllButton = <T,>({ style }: { style?: CSSProperties }) => {
    const data = useTableStore<T, 'data'>('data')
    const selectedRows = useTableStore<T, 'selectedRows'>('selectedRows')
    const { store, id } = useTableContext<T>()

    const selectAll = () => {
        const newMap = new Map(selectedRows)
        data.forEach(item => {
            newMap.set(item[id] as string | number, item)
        })
        store.update({ selectedRows: newMap })
    }
    const clearAll = () => store.update({ selectedRows: new Map() })
    const isAllSelected = () =>
        data.length > 0 &&
        [...selectedRows.keys()].filter(key => data.find(item => item[id] == key)).length ==
        data.length
    const isAnySelected = () => data.length > 0 && selectedRows.size != 0

    return (
        <Table.ColumnHeaderCell className="table__selectable-headercell" style={style}>
            <Checkbox
                checked={isAllSelected() ? true : isAnySelected() ? 'indeterminate' : false}
                onCheckedChange={state => {
                    if (state == true)
                        selectAll()
                    else
                        clearAll()
                }}
            />
        </Table.ColumnHeaderCell>
    )
}

const TableHead = <T,>() => {
    const { hideHeaderRow, serverSide, store } = useTableContext<T>()
    const { columns, leadingCells, hasActionsColumn, leadingCellStyle, columnStyle } = useTableLayout<T>()
    const refreshData = useTableStore<T, 'refreshData'>('refreshData')
    const filters = useTableStore<T, 'filters'>('filters')
    const sorter = useTableStore<T, 'sorter'>('sorter')

    const filteredColumns = Object.values(filters)
        .filter(filter => !!filter.column)
        .map(filter => filter.column)

    const t = useTranslations('table')

    const handleSortingChange = (column: string) => {
        const order = sorter
            ? column === sorter.column && sorter.order === 'asc'
                ? 'desc'
                : 'asc'
            : 'asc'
        store.update({ sorter: { column, order } })
    }

    // static data is sorted in the body
    useUpdateEffect(() => {
        if (serverSide)
            refreshData()
    }, [sorter])

    const getSortClass = (sortable: boolean | undefined, accessor: string) => {
        if (!sortable)
            return undefined

        if (sorter && sorter.column === accessor)
            return sorter.order === 'asc' ? 'up' : 'down'

        return 'default'
    }

    if (hideHeaderRow)
        return null

    const renderLeadingCell = (cell: TLeadingCell, index: number) =>
        cell === 'select'
            ? <SelectAllButton<T> key={cell} style={leadingCellStyle(index)} />
            : <Table.ColumnHeaderCell key={cell} style={leadingCellStyle(index)} />

    return (
        <Table.Header>
            <Table.Row>
                {leadingCells.map(renderLeadingCell)}
                {columns.map((column, index) => {
                    const { label, accessor, sortable } = column
                    const sortClass = getSortClass(sortable, accessor)
                    const filtered = filteredColumns.includes(generateAccessorColumn(column))
                    const style = columnStyle(index)
                    return (
                        <Table.ColumnHeaderCell
                            key={index}
                            onClick={sortable ? () => handleSortingChange(accessor) : undefined}
                            className={`${sortable ? 'sortable-column' : ''} ${sortClass ?? ''} fixed`}
                            style={style && { ...style, backgroundColor: 'var(--gray-1)' }}
                        >
                            {label}
                            {filtered && <MagnifyingGlassIcon className="filter-icon" />}
                            {sortClass &&
                                sortClass != 'default' &&
                                (sortClass == 'down' ? (
                                    <ChevronDownIcon className="sorter-icon" />
                                ) : (
                                    <ChevronUpIcon className="sorter-icon" />
                                ))}
                        </Table.ColumnHeaderCell>
                    )
                })}
                {hasActionsColumn && (
                    <Table.ColumnHeaderCell>{t('action')}</Table.ColumnHeaderCell>
                )}
            </Table.Row>
        </Table.Header>
    )
}

export default TableHead
